// StarGardenPage.jsx – Main fullscreen "Yıldız Bahçem" board game page.
// Persisted via Star Garden backend API; progress derived from action history.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { keyframes } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeOff from '@mui/icons-material/VolumeOff';
import CalendarToday from '@mui/icons-material/CalendarToday';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

import { getMeCached } from '../authCache';
import {
  fetchBoard,
  fetchClasses,
  awardStar,
  undoLastAction,
  createCriteria,
} from './api/starGardenApi';
import StudentCarousel from './StudentCarousel';
import BehaviorPanel from './BehaviorPanel';
import ConfettiOverlay from './ConfettiOverlay';

// ─── Star fly keyframe ─────────────────────────────────────
const starFlyAnim = keyframes`
  0%   { opacity: 1; transform: translate(0, 0) scale(1.5); }
  70%  { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.5); }
`;

const rainbowEnter = keyframes`
  0%   { opacity: 0; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
`;

const rainbowPulse = keyframes`
  0%   { transform: scale(1); }
  50%  { transform: scale(1.06); }
  100% { transform: scale(1); }
`;

// ─── Helpers ────────────────────────────────────────────────

// Convert backend student progress into the shape the UI cards expect
const toProgressMap = (students) => {
  const map = {};
  (students || []).forEach((s) => {
    map[String(s.student_id)] = {
      stage: s.stage ?? 0,
      stageStars: s.stage_stars ?? 0,
      badgesEarned: s.badges_earned ?? 0,
      lifetimeStars: s.lifetime_stars ?? 0,
      criteriaCounts: s.criteria_counts ?? {},
      todayUsedCriteriaIds: s.today_used_criteria_ids ?? [],
    };
  });
  return map;
};

// Convert backend student to carousel-friendly shape
const toStudentList = (students) =>
  (students || []).map((s) => ({
    id: String(s.student_id),
    firstName: s.first_name,
    lastName: s.last_name,
    classId: s.class_id,
  }));

// Format Turkish date
const formatTurkishDate = () => {
  const d = new Date();
  const months = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
  ];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

// ─── Main Page Component ────────────────────────────────────

const StarGardenPage = () => {
  const navigate = useNavigate();

  // Auth & school
  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]); // [{id, name}, ...]
  const [selectedClass, setSelectedClass] = useState(null); // {id, name}

  // Core state (from backend)
  const [criteria, setCriteria] = useState([]); // [{id, name, icon, ...}]
  const [students, setStudents] = useState([]); // [{id, firstName, ...}]
  const [progressMap, setProgressMap] = useState({}); // Record<studentId, progress>
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [actionInFlight, setActionInFlight] = useState(false); // prevent double-clicks

  // Animation / overlay state
  const [animateStageForId, setAnimateStageForId] = useState(null);
  const [flowerCriteria, setFlowerCriteria] = useState([]);
  const [confettiActive, setConfettiActive] = useState(false);
  const [flyingStar, setFlyingStar] = useState(null); // { x, y, dx, dy } or null
  const [endOfDayOverlay, setEndOfDayOverlay] = useState(false);

  // Refs
  const cardAreaRef = useRef(null);

  // ─── 1. Load auth → schoolId ─────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMeCached();
        if (cancelled) return;
        if (!me?.ok || !me.data?.admin_user) {
          setError('Oturum bilgisi alınamadı. Lütfen tekrar giriş yapın.');
          setLoading(false);
          return;
        }
        const sid = me.data.admin_user.school_id;
        if (!sid) {
          setError('Okul bilgisi bulunamadı.');
          setLoading(false);
          return;
        }
        setSchoolId(sid);
      } catch (e) {
        if (!cancelled) {
          setError('Oturum bilgisi alınamadı.');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── 2. Load classes when schoolId is known ─────
  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await fetchClasses(schoolId);
        if (cancelled) return;
        const sorted = (list || []).sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'tr-TR')
        );
        setClasses(sorted);
        // Auto-select if only one class
        if (sorted.length === 1) {
          setSelectedClass(sorted[0]);
        } else if (sorted.length > 1) {
          // Allow the class selector screen to render
          setLoading(false);
        }
        if (sorted.length === 0) {
          setError('Bu okula ait sınıf bulunamadı.');
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Sınıflar yüklenemedi.');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId]);

  // ─── 3. Load board when class is selected ─────
  const loadBoard = useCallback(async () => {
    if (!schoolId || !selectedClass) return;
    setLoading(true);
    setError(null);
    try {
      const board = await fetchBoard(schoolId, selectedClass.id);
      setCriteria(board.criteria || []);
      setStudents(toStudentList(board.students));
      setProgressMap(toProgressMap(board.students));
      setFlowerCriteria(board.flower_criteria || board.criteria || []);
      setActiveIndex(0);
    } catch (e) {
      setError('Oyun tahtası yüklenemedi: ' + (e.message || ''));
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClass]);

  useEffect(() => {
    if (selectedClass) loadBoard();
  }, [selectedClass, loadBoard]);

  // ─── Active student ─────
  const activeStudent = students[activeIndex] || null;
  const activeProgress = activeStudent
    ? (progressMap[activeStudent.id] || { stage: 0, stageStars: 0, badgesEarned: 0, lifetimeStars: 0, todayUsedCriteriaIds: [] })
    : { stage: 0, stageStars: 0, badgesEarned: 0, lifetimeStars: 0, todayUsedCriteriaIds: [] };

  // ─── Apply behavior (award star via backend) ─────
  const applyBehavior = useCallback(async (studentId, criteriaId, buttonEl) => {
    if (actionInFlight || !schoolId || !selectedClass) return;
    setActionInFlight(true);
    try {
      const result = await awardStar(schoolId, {
        studentId: Number(studentId),
        criteriaId,
        classId: selectedClass.id,
      });

      // Update local state with returned progress
      const s = result.student;
      const newProg = {
        stage: s.stage,
        stageStars: s.stage_stars,
        badgesEarned: s.badges_earned,
        lifetimeStars: s.lifetime_stars,
        criteriaCounts: {
          ...(progressMap[String(studentId)]?.criteriaCounts || {}),
          [String(criteriaId)]: Number(progressMap[String(studentId)]?.criteriaCounts?.[String(criteriaId)] || 0) + 1,
        },
        todayUsedCriteriaIds: s.today_used_criteria_ids || [],
      };

      setProgressMap((prev) => ({ ...prev, [String(studentId)]: newProg }));
      setAnimateStageForId(String(studentId));
      setTimeout(() => setAnimateStageForId(null), 700);

      // Star fly animation
      if (buttonEl && cardAreaRef.current) {
        const btnRect = buttonEl.getBoundingClientRect();
        const cardRect = cardAreaRef.current.getBoundingClientRect();
        const targetX = cardRect.left + cardRect.width / 2;
        const targetY = cardRect.top + cardRect.height * 0.55;
        const startX = btnRect.left + btnRect.width / 2;
        const startY = btnRect.top + btnRect.height / 2;
        setFlyingStar({
          x: startX,
          y: startY,
          dx: targetX - startX,
          dy: targetY - startY,
        });
        setTimeout(() => setFlyingStar(null), 300);
      }

      // Sound feedback
      if (soundEnabled) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.value = 880;
          osc.type = 'sine';
          gain.gain.value = 0.1;
          osc.connect(gain).connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.12);
        } catch (_) { /* ignore audio errors */ }
      }
    } catch (err) {
      console.error('[StarGarden] awardStar failed:', err);
      setError('Yıldız verilemedi: ' + (err.message || ''));
      setTimeout(() => setError(null), 3000);
    } finally {
      setActionInFlight(false);
    }
  }, [schoolId, selectedClass, soundEnabled, progressMap, actionInFlight]);

  // ─── Undo last action for active student ─────
  const undoLast = useCallback(async (studentId) => {
    if (actionInFlight || !schoolId) return;
    setActionInFlight(true);
    try {
      const result = await undoLastAction(schoolId, Number(studentId));
      if (result.student) {
        const board = await fetchBoard(schoolId, selectedClass.id);
        setProgressMap(toProgressMap(board.students));
      }
    } catch (err) {
      console.error('[StarGarden] undoLast failed:', err);
      setError('Geri alma başarısız: ' + (err.message || ''));
      setTimeout(() => setError(null), 3000);
    } finally {
      setActionInFlight(false);
    }
  }, [schoolId, selectedClass, actionInFlight]);

  // ─── Add custom criteria ─────
  const handleAddCriteria = useCallback(async (name) => {
    if (!schoolId || !selectedClass) return;
    try {
      await createCriteria(schoolId, { name, classId: selectedClass.id });
      // Reload board to pick up new criteria
      await loadBoard();
    } catch (err) {
      console.error('[StarGarden] addCriteria failed:', err);
      setError('Kriter eklenemedi: ' + (err.message || ''));
      setTimeout(() => setError(null), 3000);
    }
  }, [schoolId, selectedClass, loadBoard]);

  // ─── Bottom "Bugün Harikaydık" handler ─────
  const handleEndOfDay = useCallback(() => {
    setEndOfDayOverlay(true);
    setConfettiActive(true);
  }, []);

  // Dismiss end-of-day overlay on click
  const dismissEndOfDay = useCallback(() => {
    setEndOfDayOverlay(false);
    setConfettiActive(false);
  }, []);

  // ─── Derived ─────
  const todayLabel = useMemo(() => formatTurkishDate(), []);

  // ─── Class selector screen (multi-class teacher) ─────
  if (!loading && schoolId && classes.length > 1 && !selectedClass) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          bgcolor: '#e8f5e9',
          userSelect: 'none',
          p: 4,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2e7d32', mb: 1 }}>
          🌼 YILDIZ BAHÇEM
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
          Sınıfınızı seçin
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', maxWidth: 600 }}>
          {classes.map((cls) => (
            <Button
              key={cls.id}
              variant="contained"
              onClick={() => setSelectedClass(cls)}
              sx={{
                px: 4,
                py: 2,
                fontSize: 18,
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #66bb6a, #43a047)',
                color: '#fff',
                minWidth: 120,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4caf50, #388e3c)',
                },
              }}
            >
              {cls.name}
            </Button>
          ))}
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/stargarden/batch')}
          sx={{
            mt: 1,
            textTransform: 'none',
            borderRadius: 3,
            fontWeight: 700,
            borderColor: '#2e7d32',
            color: '#2e7d32',
            '&:hover': { bgcolor: '#e8f5e9', borderColor: '#1b5e20' },
          }}
        >
          ⚡ Yıldız Bahçem Hızlı
        </Button>
        <Button
          variant="text"
          onClick={() => navigate('/admin')}
          sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
        >
          ← Panele dön
        </Button>
      </Box>
    );
  }

  // ─── Error state ─────
  if (error && !loading && students.length === 0) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          bgcolor: '#e8f5e9',
          userSelect: 'none',
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>{error}</Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin')}
          sx={{ textTransform: 'none' }}
        >
          Panele dön
        </Button>
      </Box>
    );
  }

  // ─── Loading state ─────
  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          bgcolor: '#e8f5e9',
          userSelect: 'none',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#4caf50' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#2e7d32' }}>
          🌼 Yıldız Bahçem yükleniyor...
        </Typography>
      </Box>
    );
  }

  if (students.length === 0) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#e8f5e9',
        }}
      >
        <Typography variant="h5" sx={{ color: '#2e7d32' }}>
          Öğrenci bulunamadı.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',
        userSelect: 'none',
        bgcolor: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)',
        background: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)',
        p: 'clamp(8px, 1.5vw, 40px)',
      }}
    >
      {/* ──── TOP BAR ──── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1, md: 3 },
          py: 1,
          flexShrink: 0,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(8px)',
          boxShadow: 1,
          mb: 1,
        }}
      >
        {/* Left: back + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Panele dön">
            <IconButton onClick={() => navigate('/admin')} size="small">
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontSize: 'clamp(18px, 2.5vw, 32px)',
                color: '#2e7d32',
                lineHeight: 1.2,
              }}
            >
              🌼 YILDIZ BAHÇEM
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: 'clamp(11px, 1.1vw, 15px)',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Her güzel davranış için çiçeğimize yıldız ekliyoruz!
            </Typography>
          </Box>
        </Box>

        {/* Right: date + class name + sound */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Today label */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: 'rgba(46,125,50,0.08)',
            }}
          >
            <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 'clamp(11px, 1vw, 14px)' }}>
              {todayLabel}
            </Typography>
          </Box>

          {/* Class name badge */}
          {selectedClass && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: 'rgba(46,125,50,0.12)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 'clamp(11px, 1vw, 14px)', color: '#2e7d32' }}>
                📚 {selectedClass.name}
              </Typography>
            </Box>
          )}

          {/* Switch class (only if multiple) */}
          {classes.length > 1 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setSelectedClass(null); setStudents([]); setCriteria([]); }}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: 'clamp(11px, 1vw, 14px)',
                borderColor: '#ff9800',
                color: '#e65100',
                '&:hover': { bgcolor: '#fff3e0', borderColor: '#e65100' },
              }}
            >
              Sınıf Değiştir
            </Button>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/admin/stargarden/batch')}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 'clamp(11px, 1vw, 14px)',
              borderColor: '#2e7d32',
              color: '#2e7d32',
              '&:hover': { bgcolor: '#e8f5e9', borderColor: '#1b5e20' },
            }}
          >
            ⚡ Yıldız Bahçem Hızlı
          </Button>

          {/* Sound toggle */}
          <Tooltip title={soundEnabled ? 'Sesi kapat' : 'Sesi aç'}>
            <IconButton onClick={() => setSoundEnabled((v) => !v)} size="small">
              {soundEnabled ? <VolumeUp /> : <VolumeOff />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ──── MAIN CONTENT ──── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1, md: 2 },
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: { xs: 'auto', md: 'hidden' },
        }}
      >
        {/* Left/Center: Carousel */}
        <Box
          ref={cardAreaRef}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 0,
          }}
        >
          <StudentCarousel
            students={students}
            activeIndex={activeIndex}
            onChangeIndex={(idx) => {
              setActiveIndex(idx);
            }}
            progressMap={progressMap}
            animateStageForId={animateStageForId}
            flowerCriteria={flowerCriteria}
          />
        </Box>

        {/* Right: Behavior panel */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            flexShrink: 0,
            minHeight: 0,
            overflowY: { xs: 'visible', md: 'auto' },
            px: { xs: 0, md: 1 },
          }}
        >
          {activeStudent && (
            <BehaviorPanel
              criteria={criteria}
              todayUsedCriteriaIds={activeProgress.todayUsedCriteriaIds || []}
              onBehaviorClick={(criteriaId, btnEl) =>
                applyBehavior(activeStudent.id, criteriaId, btnEl)
              }
              onUndo={() => undoLast(activeStudent.id)}
              undoDisabled={activeProgress.lifetimeStars === 0 || actionInFlight}
              studentName={activeStudent.firstName}
              onAddCriteria={handleAddCriteria}
            />
          )}
        </Box>
      </Box>

      {/* ──── BOTTOM BAR ──── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 1,
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={handleEndOfDay}
          sx={{
            borderRadius: 4,
            px: { xs: 3, md: 6 },
            py: 1.5,
            fontSize: 'clamp(15px, 1.6vw, 22px)',
            fontWeight: 700,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #66bb6a, #43a047)',
            color: '#fff',
            boxShadow: 3,
            '&:hover': {
              background: 'linear-gradient(135deg, #4caf50, #388e3c)',
            },
          }}
        >
          Bugün Harikaydık! 🌈
        </Button>
      </Box>

      {/* ──── OVERLAYS ──── */}

      {/* Confetti */}
      <ConfettiOverlay
        active={confettiActive}
        duration={800}
        onDone={() => setConfettiActive(false)}
      />

      {/* End-of-day overlay – click anywhere to dismiss */}
      {endOfDayOverlay && (
        <Box
          onClick={dismissEndOfDay}
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
            zIndex: 9997,
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.5)',
              borderRadius: 5,
              px: 6,
              py: 5,
              boxShadow: 8,
              textAlign: 'center',
              backdropFilter: 'blur(12px)',
              animation: `${rainbowEnter} 600ms ease-out forwards, ${rainbowPulse} 2.4s ease-in-out 600ms infinite`,
            }}
          >
            <Typography sx={{ fontSize: 52 }}>🌼</Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, mt: 1, color: '#2e7d32', fontSize: 'clamp(20px, 3vw, 36px)' }}
            >
              Yarın yeni yıldızlar biriktireceğiz 🌼
            </Typography>
          </Box>
        </Box>
      )}

      {/* Flying star particle */}
      {flyingStar && (
        <Box
          sx={{
            position: 'fixed',
            left: flyingStar.x,
            top: flyingStar.y,
            zIndex: 10000,
            pointerEvents: 'none',
            fontSize: 32,
            '--dx': `${flyingStar.dx}px`,
            '--dy': `${flyingStar.dy}px`,
            animation: `${starFlyAnim} 280ms ease-in forwards`,
          }}
        >
          ⭐
        </Box>
      )}

      {/* Error toast */}
      {error && students.length > 0 && (
        <Box sx={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10001 }}>
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        </Box>
      )}
    </Box>
  );
};

export default StarGardenPage;
